import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { simulateAnalysis } from '../services/staticAnalysis';
import Header from '../components/Header';
import '../App.css';

const Mahan01 = () => {
  const [activeSection, setActiveSection] = useState('thesis');
  const [selectedNation, setSelectedNation] = useState('england');
  const [modalContent, setModalContent] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState('✅ Conectado');

  // Datos de las 6 elementos del poder naval
  const elementsData = {
    england: {
      name: 'Inglaterra',
      color: '#1B365D',
      scores: [95, 85, 70, 80, 90, 95], // Posición, Física, Territorio, Población, Carácter, Gobierno
      description: 'La potencia modelo. Posición insular perfecta.'
    },
    france: {
      name: 'Francia',
      color: '#3B82F6',
      scores: [60, 75, 85, 90, 70, 65],
      description: 'El gigante distraído. Rica, pero dividida.'
    },
    holland: {
      name: 'Holanda',
      color: '#F59E0B',
      scores: [80, 90, 40, 70, 95, 75],
      description: 'Comerciantes natos, territorio vulnerable.'
    }
  };

  const elements = [
    'Posición Geográfica',
    'Conformación Física', 
    'Extensión Territorial',
    'Población Marítima',
    'Carácter Nacional',
    'Gobierno'
  ];

  // Conceptos estratégicos para análisis IA
  const strategicConcepts = [
    {
      id: 'guerre_de_course',
      title: 'Guerre de Course',
      icon: '⚔️',
      description: 'Guerra al comercio. El arma de los débiles. Mahan argumenta que molesta pero nunca gana guerras.',
      context: 'Mahan critica severamente el ataque al comercio como estrategia principal. Argumenta que aunque destruye riqueza y causa sufrimiento, nunca gana guerras por sí solo si no se tiene el control del mar.'
    },
    {
      id: 'fleet_in_being',
      title: 'Fleet in Being',
      icon: '⚓',
      description: 'Mantener una flota activa evitando batalla. Estrategia defensiva para paralizar al enemigo.',
      context: 'Concepto ejemplificado por el Almirante Torrington. Se refiere a mantener una flota naval activa y amenazante, pero evitando el combate decisivo hasta que las condiciones sean favorables.'
    },
    {
      id: 'choke_points',
      title: 'Choke Points',
      icon: '🗝️',
      description: 'Puntos de estrangulamiento. El control geográfico de rutas obligatorias como Gibraltar.',
      context: 'Estrechos o canales geográficos obligatorios para el comercio y el movimiento naval. Mahan argumenta que controlar estos puntos es infinitamente más valioso que controlar vastas extensiones de océano abierto.'
    },
    {
      id: 'line_of_battle',
      title: 'Línea de Batalla',
      icon: '📏',
      description: 'Orden frente al caos. La evolución táctica para maximizar la artillería naval.',
      context: 'La formación táctica estándar desde mediados del siglo XVII. Los barcos navegan en fila india para maximizar el fuego de sus costados sin bloquearse mutuamente.'
    }
  ];

  // Datos históricos
  const historicalData = [
    {
      period: '1660-1674',
      title: 'Guerras Anglo-Holandesas',
      control: 75,
      description: 'Inglaterra desplaza a Holanda como potencia naval dominante. Los holandeses, aunque comerciantes supremos, tenían territorio vulnerable a invasiones terrestres.'
    },
    {
      period: '1689-1763',
      title: 'Ascenso de Inglaterra',
      control: 85,
      description: 'A través de múltiples guerras, Inglaterra aplicó una estrategia constante: adquirir bases avanzadas y destruir la flota enemiga organizada.'
    },
    {
      period: '1756-1763',
      title: 'Guerra de los Siete Años',
      control: 95,
      description: 'El clímax del poder naval inglés. La aniquilación de la flota francesa en Quiberon Bay permitió conquistar Canadá y la India sin oposición.'
    },
    {
      period: '1775-1783',
      title: 'Independencia Americana',
      control: 45,
      description: 'La gran advertencia. Inglaterra permitió que su armada decayera. Francia logró superioridad local temporal en Chesapeake, aislando al ejército británico en Yorktown.'
    }
  ];

  const handleAnalyzeConcept = async (concept) => {
    setIsLoading(true);
    setIsModalOpen(true);
    setModalContent('Analizando concepto naval...');

    try {
      const response = await simulateAnalysis('mahan', concept.title);
      setModalContent(response);
    } catch (error) {
      console.error('Error analyzing concept:', error);
      setModalContent(`**Error de Análisis:** No se pudo cargar el análisis.\n\n*Por favor intenta nuevamente.*`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderThesis = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
      {/* Texto principal */}
      <div>
        <h3 style={{ color: '#f1f5f9', fontSize: '2rem', marginBottom: '1rem' }}>
          El Mar: Una Vía, No Una Barrera
        </h3>
        <p style={{ color: '#cbd5e1', lineHeight: '1.8', marginBottom: '1.5rem' }}>
          Mahan no escribe sobre batallas por la gloria, sino sobre economía y logística. Su tesis central es que el <strong>Poder Naval</strong> es el factor decisivo en la prosperidad nacional. El mar es un "gran bien común" y quien controla esta vía, controla el flujo de riqueza del mundo.
        </p>
        
        {/* Cadena del Poder Naval */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          padding: '1.5rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <h4 style={{ color: '#fbbf24', fontSize: '1.2rem', marginBottom: '1rem' }}>
            La Cadena del Poder Naval
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '2rem' }}>🏭</span>
              <div>
                <strong style={{ color: '#f1f5f9' }}>1. Producción Nacional</strong>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>La base económica en casa. Necesidad de exportar.</p>
              </div>
            </div>
            <div style={{ textAlign: 'center', color: '#64748b' }}>⬇️</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '2rem' }}>🚢</span>
              <div>
                <strong style={{ color: '#f1f5f9' }}>2. Marina Mercante</strong>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>El eslabón vital. Transporta la riqueza y crea marinos.</p>
              </div>
            </div>
            <div style={{ textAlign: 'center', color: '#64748b' }}>⬇️</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '2rem' }}>🌍</span>
              <div>
                <strong style={{ color: '#f1f5f9' }}>3. Mercados y Colonias</strong>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>El destino. Intercambio de bienes y recursos.</p>
              </div>
            </div>
            <div style={{ textAlign: 'center', color: '#64748b' }}>⬇️</div>
            <div style={{
              background: '#1B365D',
              borderRadius: '0.5rem',
              padding: '1rem',
              textAlign: 'center',
              border: '2px solid #fbbf24'
            }}>
              <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}>🛡️</span>
              <strong style={{ color: '#fbbf24' }}>4. Marina Militar (Armada)</strong>
              <p style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
                Su ÚNICA función es proteger la línea de comunicación entre los puntos 1, 2 y 3.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Retrato de Mahan */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.8)',
        borderRadius: '1rem',
        padding: '2rem',
        textAlign: 'center',
        border: '1px solid rgba(148, 163, 184, 0.2)'
      }}>
        <div style={{
          width: '200px',
          height: '250px',
          background: 'linear-gradient(135deg, #1B365D 0%, #345888 100%)',
          borderRadius: '1rem',
          margin: '0 auto 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '4rem',
          border: '3px solid #fbbf24'
        }}>
          ⚓
        </div>
        <h4 style={{ color: '#f1f5f9', fontSize: '1.3rem', marginBottom: '0.5rem' }}>
          Alfred Thayer Mahan
        </h4>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          1840 – 1914
        </p>
        <p style={{ color: '#fbbf24', fontSize: '0.8rem', fontStyle: 'italic', marginBottom: '1rem' }}>
          "El historiador naval más influyente de todos los tiempos."
        </p>
        <p style={{ color: '#64748b', fontSize: '0.8rem', textAlign: 'justify' }}>
          Sus teorías influyeron en Teodoro Roosevelt, el Káiser Guillermo II y la estrategia naval japonesa. Definió el control del mar como la clave de la historia moderna.
        </p>
      </div>
    </div>
  );

  const renderElements = () => (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
        {/* Controles */}
        <div>
          <h4 style={{ color: '#f1f5f9', fontSize: '1.2rem', marginBottom: '1rem' }}>
            Seleccionar Nación
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {Object.entries(elementsData).map(([key, nation]) => (
              <button
                key={key}
                onClick={() => setSelectedNation(key)}
                style={{
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  border: selectedNation === key ? `2px solid ${nation.color}` : '1px solid rgba(148, 163, 184, 0.2)',
                  background: selectedNation === key ? `${nation.color}20` : 'rgba(30, 41, 59, 0.8)',
                  color: selectedNation === key ? nation.color : '#cbd5e1',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <strong>{nation.name}</strong>
                <p style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '0.25rem' }}>
                  {nation.description}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Radar Chart Simulado */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          padding: '2rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <h4 style={{ color: '#f1f5f9', fontSize: '1.2rem', marginBottom: '1.5rem', textAlign: 'center' }}>
            Los 6 Elementos: {elementsData[selectedNation].name}
          </h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            {elements.map((element, index) => (
              <div key={index} style={{
                background: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <h5 style={{ color: '#f1f5f9', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  {element}
                </h5>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'rgba(148, 163, 184, 0.2)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${elementsData[selectedNation].scores[index]}%`,
                    height: '100%',
                    background: elementsData[selectedNation].color,
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  {elementsData[selectedNation].scores[index]}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div>
      <div style={{
        background: 'rgba(30, 41, 59, 0.8)',
        borderRadius: '1rem',
        padding: '2rem',
        marginBottom: '2rem',
        border: '1px solid rgba(148, 163, 184, 0.2)'
      }}>
        <h4 style={{ color: '#fbbf24', fontSize: '1.3rem', marginBottom: '1rem' }}>
          ¿Qué es el "Dominio del Mar"?
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          <div>
            <strong style={{ color: '#f1f5f9', display: 'block', marginBottom: '0.5rem' }}>
              ¿Control de qué?
            </strong>
            <p style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
              No se trata de ocupar cada metro cuadrado de océano. Se trata de controlar las <strong>Líneas de Comunicación</strong>. El mar es una carretera: el "Dominio" significa que tus barcos pueden viajar libremente.
            </p>
          </div>
          <div>
            <strong style={{ color: '#f1f5f9', display: 'block', marginBottom: '0.5rem' }}>
              ¿Control sobre quién?
            </strong>
            <p style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
              Sobre la capacidad del enemigo para sobrevivir. Si controlas el mar, <strong>aíslas</strong> sus colonias y <strong>asfixias</strong> su comercio. Incluso un ejército poderoso colapsa sin suministros.
            </p>
          </div>
        </div>
      </div>

      <h4 style={{ color: '#f1f5f9', fontSize: '1.2rem', marginBottom: '1.5rem' }}>
        Histórico de Control Naval Británico (1660-1783)
      </h4>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
        {historicalData.map((period, index) => (
          <div key={index} style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            borderLeft: `4px solid ${period.control > 80 ? '#10b981' : period.control > 60 ? '#fbbf24' : '#ef4444'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h5 style={{ color: '#f1f5f9', fontSize: '1rem' }}>{period.period}</h5>
              <span style={{ 
                color: period.control > 80 ? '#10b981' : period.control > 60 ? '#fbbf24' : '#ef4444',
                fontWeight: 'bold'
              }}>
                {period.control}%
              </span>
            </div>
            <h6 style={{ color: '#fbbf24', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              {period.title}
            </h6>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
              {period.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStrategy = () => (
    <div>
      <h4 style={{ color: '#f1f5f9', fontSize: '1.2rem', marginBottom: '1.5rem' }}>
        Enciclopedia Estratégica
      </h4>
      <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>
        Conceptos fundamentales de Mahan. Haz clic en las tarjetas para análisis profundo con IA.
      </p>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {strategicConcepts.map((concept, index) => (
          <div key={concept.id} 
            onClick={() => handleAnalyzeConcept(concept)}
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontSize: '2rem', marginRight: '1rem' }}>{concept.icon}</span>
              <h5 style={{ color: '#f1f5f9', fontSize: '1.1rem', fontWeight: 'bold' }}>
                {concept.title}
              </h5>
            </div>
            
            <p style={{ color: '#cbd5e1', marginBottom: '1rem', fontSize: '0.9rem' }}>
              {concept.description}
            </p>
            
            <div style={{ 
              padding: '0.5rem 1rem', 
              background: 'rgba(59, 130, 246, 0.2)', 
              borderRadius: '0.25rem',
              textAlign: 'center'
            }}>
              <span style={{ color: '#93c5fd', fontSize: '0.8rem' }}>
                🤖 Click para análisis con IA
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }}>
      <Header />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <Link to="/">
            <button style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}>
              ← Volver al Inicio
            </button>
          </Link>
        </div>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ 
            marginBottom: '0.5rem', 
            color: '#fbbf24', 
            fontWeight: 'bold', 
            letterSpacing: '0.1em', 
            textTransform: 'uppercase', 
            fontSize: '0.875rem' 
          }}>
            {authStatus}
          </div>
          <h1 style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            marginBottom: '0.5rem',
            color: '#f1f5f9',
            fontFamily: 'serif'
          }}>
            ⚓ Análisis Estratégico
          </h1>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '300', 
            fontStyle: 'italic', 
            opacity: 0.9,
            color: '#cbd5e1'
          }}>
            El Poder Naval en la Historia (1660-1783)
          </h2>
          <p style={{ 
            marginTop: '1rem', 
            fontSize: '1.125rem', 
            fontWeight: '600', 
            color: '#94a3b8' 
          }}>
            Alfred Thayer Mahan
          </p>
        </div>

        {/* Navigation Tabs */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {[
            { id: 'thesis', label: 'La Tesis', icon: '📋' },
            { id: 'elements', label: 'Los 6 Elementos', icon: '⚖️' },
            { id: 'history', label: 'Historia Naval', icon: '📚' },
            { id: 'strategy', label: 'Estrategia', icon: '🎯' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: activeSection === tab.id 
                  ? 'linear-gradient(135deg, #1B365D 0%, #345888 100%)'
                  : 'rgba(30, 41, 59, 0.8)',
                color: activeSection === tab.id ? '#fbbf24' : '#cbd5e1',
                cursor: 'pointer',
                fontWeight: activeSection === tab.id ? 'bold' : 'normal',
                transition: 'all 0.3s ease',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          minHeight: '500px'
        }}>
          {activeSection === 'thesis' && renderThesis()}
          {activeSection === 'elements' && renderElements()}
          {activeSection === 'history' && renderHistory()}
          {activeSection === 'strategy' && renderStrategy()}
        </div>

        {/* Navigation */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          gap: '1rem',
          marginTop: '3rem', 
          padding: '2rem',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <Link to="/">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              ← Volver al Dashboard
            </button>
          </Link>
          
          <Link to="/mahan02">
            <button style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'linear-gradient(135deg, #1B365D 0%, #345888 100%)',
              color: '#fbbf24',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              Filosofía Naval ➡️
            </button>
          </Link>
        </div>
      </div>

      {/* Modal para análisis IA */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              padding: '2rem',
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem' }}>
                ⚓ Análisis Naval con IA
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              {isLoading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚓</div>
                  <p>Analizando estrategia naval...</p>
                </div>
              ) : (
                <div 
                  style={{ 
                    color: '#cbd5e1',
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: modalContent
                      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #f1f5f9;">$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                      .replace(/\n\n/g, '</p><p style="margin: 1rem 0;">')
                      .replace(/^/, '<p style="margin: 1rem 0;">')
                      .replace(/$/, '</p>')
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mahan01;